import React, { useMemo } from 'react';
import {
  Avatar,
  Button,
  Space,
  Divider,
  Dropdown,
} from 'antd';
import {
  ArrowRightIcon,
  DefaultCompanyIcon,
  ActivityIcon,
  SettingsIcon,
  LanguageIcon,
  AcademyIcon,
  KnowledgeBaseIcon,
  EmailIcon,
  PhoneIcon,
  LogoutIcon,
  QuestionIcon,
  ZendeskIcon,
} from 'components/cwo-icons';
import { isEmpty } from 'lodash-es';
import TopMenuFieldGroups from 'components/top-menu-field-groups';
import TopMenuInstantNotifications from 'components/top-menu-instant-notifications';
import {
  bool, func, number, shape, string,
} from 'prop-types';
import { buildSection, divider } from '../helpers';
import { topMenuOptionsPropTypes, topMenuOptionsDefaultPropTypes } from '../propTypes';
import MenuLink from './MenuLink';

const icons = {
  'account-activity': <ActivityIcon />,
  'account-settings': <SettingsIcon />,
  'account-language': <LanguageIcon />,
  'account-sign-out': <LogoutIcon />,
  'company-company': <DefaultCompanyIcon />,
  'knowledge-base': <KnowledgeBaseIcon />,
  'cropio-academy': <AcademyIcon />,
  'zendesk-widget': <ZendeskIcon />,
  email: <EmailIcon />,
  phone: <PhoneIcon />,
};

const RightPart = ({
  topMenuOptions = topMenuOptionsDefaultPropTypes,
  currentLocale,
  filterDrawerOpen = false,
  setFilterDrawerOpen,
  filterCollapseActiveKey = 1,
  setFilterCollapseActiveKey,
}) => {
  const {
    filter,
    question,
    notifications,
    settings,
    account,
  } = topMenuOptions;

  const buildIcon = (item) => {
    const { key, logoSrc, label } = item;
    const isImage = typeof logoSrc === 'string' && logoSrc.includes('/');

    if (['reseller'].includes(key)) {
      return (
        <img
          className="navigation-account-management__reseller-logo"
          src={logoSrc}
          alt={key}
        />
      );
    }

    if (['company-company'].includes(key)) {
      return (
        <Avatar
          size="large"
          shape="square"
          className="navigation-account-management__avatar"
          icon={isImage ? <img src={logoSrc} alt={label} /> : icons[key]}
        />
      );
    }

    if (['account-user', 'account_section'].includes(key)) {
      return (
        <Avatar
          size="large"
          shape="circle"
          className="navigation-account-management__avatar"
          icon={isImage ? <img src={logoSrc} alt={label} /> : item.initials}
        />
      );
    }

    return icons[key];
  };

  const changeLanguage = (locale) => {
    const currentUrl = new URL(globalThis.location.href);

    currentUrl.searchParams.set('locale', locale);
    globalThis.location.href = currentUrl.toString();
  };

  const buildLanguageItems = (items) => {
    if (!items) return {};

    return {
      popupOffset: [2, -160],
      popupClassName: 'navigation-dropdown-menu-account__language-popup',
      className: 'navigation-dropdown-menu-account__language-item',
      children: items.map((item) => ({
        key: item.key,
        disabled: currentLocale === item.key,
        label: (
          <a
            onClick={() => changeLanguage(item.key)}
            className="navigation-side-menu__link"
            href={item.path}
          >
            {item.label}
          </a>
        ),
      })),
    };
  };

  const buildDropdownItemGroup = (section, sectionGroupKey) => {
    const { [sectionGroupKey]: sectionGroup } = section;
    if (!section) return {};

    return (
      {
        key: sectionGroup.key,
        type: 'group',
        label: sectionGroup.label,
        children: sectionGroup.items.map((item) => {
          const isMultilogin = item.key === 'company-company' && item.memberships.length > 1;
          const nonClickableItem = [
            'reseller',
            isMultilogin ? '' : 'company-company',
          ].includes(item.key) ? 'non-clickable-item' : '';

          if (item.type === 'divider') return divider;

          return {
            key: item.key,
            icon: buildIcon(item),
            label: <MenuLink item={item} currentLocale={currentLocale} />,
            className: `${nonClickableItem}`,
            ...buildLanguageItems(item.children),
          };
        }),
      }
    );
  };

  const questionMenu = useMemo(() => ({
    items: buildSection(question, buildDropdownItemGroup),
    className: 'navigation-dropdown-menu-question',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const settingsMenu = useMemo(() => ({
    items: buildSection(settings, buildDropdownItemGroup),
    className: 'navigation-dropdown-menu-settings',

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const accountMenu = useMemo(() => ({
    items: buildSection(account, buildDropdownItemGroup),
    defaultSelectedKeys: [currentLocale],
    triggerSubMenuAction: 'click',
    expandIcon: <ArrowRightIcon />,
    className: 'navigation-dropdown-menu-account',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <Space className="navigation-top-menu-right-part">
      <TopMenuFieldGroups
        options={filter}
        filterDrawerOpen={filterDrawerOpen}
        setFilterDrawerOpen={setFilterDrawerOpen}
        filterCollapseActiveKey={filterCollapseActiveKey}
        setFilterCollapseActiveKey={setFilterCollapseActiveKey}
      />

      <Divider type="vertical" />

      <Space className="navigation-account-management">
        <TopMenuInstantNotifications
          alertsNotifications={notifications.alertsNotifications}
          instantNotifications={notifications.instantNotifications}
        />

        <Dropdown
          overlayClassName="navigation-dropdown-container__question"
          menu={questionMenu}
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<QuestionIcon />}
            className="navigation-account-management__question-button"
          />
        </Dropdown>

        {!isEmpty(settings) && (
          <Dropdown
            overlayClassName="navigation-dropdown-container__settings"
            menu={settingsMenu}
            trigger={['click']}
          >
            <Button
              type="text"
              icon={<SettingsIcon />}
              className="navigation-account-management__settings-button"
            />
          </Dropdown>
        )}

        <Dropdown
          overlayClassName="navigation-dropdown-container__account"
          trigger={['click']}
          menu={{ ...accountMenu, triggerSubMenuAction: 'click' }}
        >
          <Button
            type="link"
            icon={buildIcon(account)}
            className="navigation-account-management__avatar-button"
          />
        </Dropdown>
      </Space>
    </Space>
  );
};

RightPart.propTypes = {
  topMenuOptions: shape(topMenuOptionsPropTypes),
  currentLocale: string.isRequired,
  filterDrawerOpen: bool,
  setFilterDrawerOpen: func.isRequired,
  filterCollapseActiveKey: number,
  setFilterCollapseActiveKey: func.isRequired,
};

export default RightPart;
